import * as React from 'react';
import { Button, Input, Row, Col, Space, Typography } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { DiscountBandTypeEnum, DiscountValueType } from 'app/shared/model';
import { EditableDiscountBand } from 'app/pages/DiscountEditor/modules/DiscountActions';
import {
    DISCOUNT_REQUIRED_FIELD_MESSAGE,
    DISCOUNT_MONETARY_INPUT_REGEX,
    DISCOUNT_PERCENT_INPUT_REGEX,
    normalizeMonetaryDiscountOnBlur,
    normalizePercentDiscountOnBlur,
    revalidateEditableDiscountBand,
    clampMonetaryDiscountInput,
    clampPercentDiscountInput,
    shouldShowMonetaryDiscountValueMessageInline,
} from 'app/pages/DiscountEditor/components/EditDiscountActionDialog/discountBandFieldValidation';

const { Text } = Typography;

function isAllowedBandValue(value: string) {
    return /^[0-9]*$/.test(value);
}

function setFrom(
    band: Readonly<EditableDiscountBand>,
    value: string,
    validateAndNormalize: boolean,
    discountValueType: DiscountValueType
): EditableDiscountBand {
    if (!isAllowedBandValue(value)) return band;
    if (validateAndNormalize && value !== '') value = parseInt(value, 10).toString();
    if (value === band.from) return band;

    const newBand: EditableDiscountBand = {
        ...band,
        from: value,
    };
    return revalidateEditableDiscountBand(newBand, discountValueType, DiscountBandTypeEnum.Compound);
}

function setTo(
    band: Readonly<EditableDiscountBand>,
    value: string,
    validateAndNormalize: boolean,
    discountValueType: DiscountValueType
): EditableDiscountBand {
    if (!isAllowedBandValue(value)) return band;
    if (validateAndNormalize && value !== '') value = parseInt(value, 10).toString();
    if (value === band.to) return band;

    const newBand: EditableDiscountBand = {
        ...band,
        to: value,
    };
    return revalidateEditableDiscountBand(newBand, discountValueType, DiscountBandTypeEnum.Compound);
}

function setValue(
    band: Readonly<EditableDiscountBand>,
    value: string,
    valueType: DiscountValueType,
    validateAndNormalize: boolean
): EditableDiscountBand {
    switch (valueType) {
        case DiscountValueType.AmountPerUnit:
        case DiscountValueType.OverrideUnitPrice:
            value = clampMonetaryDiscountInput(value);
            if (value !== '' && !DISCOUNT_MONETARY_INPUT_REGEX.test(value)) return band;
            if (validateAndNormalize && value !== '') value = normalizeMonetaryDiscountOnBlur(value);
            break;
        case DiscountValueType.Percent:
            value = clampPercentDiscountInput(value);
            if (value !== '' && !DISCOUNT_PERCENT_INPUT_REGEX.test(value)) return band;
            if (validateAndNormalize && value !== '') value = normalizePercentDiscountOnBlur(value);
            break;
        default:
            throw new Error('Unknown value type');
    }
    if (value === band.value) return band;

    const newBand: EditableDiscountBand = {
        ...band,
        value: value,
    };
    return revalidateEditableDiscountBand(newBand, valueType, DiscountBandTypeEnum.Compound);
}

const EmptyCulmulativeBand: Readonly<EditableDiscountBand> = {
    from: '',
    to: '',
    value: '',
    isValid: false,
    fromMessage: DISCOUNT_REQUIRED_FIELD_MESSAGE,
    toMessage: DISCOUNT_REQUIRED_FIELD_MESSAGE,
    valueMessage: DISCOUNT_REQUIRED_FIELD_MESSAGE,
};

interface CumulativeBandProps {
    band: Readonly<EditableDiscountBand>;
    showValidation: boolean;
    readOnly: boolean;
    canDelete: boolean;
    discountValueType: DiscountValueType;
    index: number;
    valueLeftLabel?: string;
    valueRightLabel?: string;
    changeFrom: (normalize: boolean, index: number, evt: React.SyntheticEvent<HTMLInputElement>) => void;
    changeTo: (normalize: boolean, index: number, evt: React.SyntheticEvent<HTMLInputElement>) => void;
    changeValue: (normalize: boolean, index: number, evt: React.SyntheticEvent<HTMLInputElement>) => void;
    deleteTier: (index: number) => void;
}

function errorLabel(showValidation: boolean, errorMessage: string | undefined) {
    if (!showValidation || errorMessage === '' || errorMessage === undefined) return null;
    return (
        <Text type="danger" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
            {errorMessage}
        </Text>
    );
}

function CulmativeBand(props: CumulativeBandProps) {
    const band = props.band;
    const index = props.index;

    const showValueMessage = shouldShowMonetaryDiscountValueMessageInline(
        props.showValidation,
        props.discountValueType,
        band.valueMessage
    );

    const valueTitle =
        props.discountValueType === DiscountValueType.Percent
            ? 'Percent off'
            : props.discountValueType === DiscountValueType.OverrideUnitPrice
              ? 'Unit price'
              : 'Amount off';

    const addonAfter =
        props.discountValueType === DiscountValueType.Percent
            ? props.valueRightLabel
            : index === 0
              ? undefined
              : props.valueRightLabel;

    return (
        <Row gutter={16} align="top" style={{ marginBottom: 20 }}>
            <Col span={7}>
                <div>
                    <Text strong style={{ fontSize: 14, color: '#262626', display: 'block', marginBottom: 6 }}>
                        From units
                    </Text>
                    <Input
                        value={band.from}
                        onChange={(e) => props.changeFrom(false, index, e)}
                        onBlur={(e) => props.changeFrom(true, index, e)}
                        disabled={props.readOnly}
                        status={props.showValidation && band.fromMessage ? 'error' : ''}
                        style={{ width: '100%' }}
                    />
                    {errorLabel(props.showValidation, band.fromMessage)}
                </div>
            </Col>
            <Col span={7}>
                <div>
                    <Text strong style={{ fontSize: 14, color: '#262626', display: 'block', marginBottom: 6 }}>
                        To units
                    </Text>
                    <Input
                        value={band.to}
                        onChange={(e) => props.changeTo(false, index, e)}
                        onBlur={(e) => props.changeTo(true, index, e)}
                        status={props.showValidation && band.toMessage ? 'error' : ''}
                        style={{ width: '100%' }}
                    />
                    {errorLabel(props.showValidation, band.toMessage)}
                </div>
            </Col>
            <Col span={7}>
                <div>
                    <Text strong style={{ fontSize: 14, color: '#262626', display: 'block', marginBottom: 6 }}>
                        {valueTitle}
                    </Text>
                {addonAfter ? (
                    <Space.Compact style={{ width: '100%' }}>
                        <Input
                            value={band.value}
                            onChange={(e) => props.changeValue(false, index, e)}
                            onBlur={(e) => props.changeValue(true, index, e)}
                            prefix={props.valueLeftLabel}
                            status={showValueMessage && band.valueMessage ? 'error' : ''}
                            style={{ width: '100%' }}
                        />
                        <Space.Addon>{addonAfter}</Space.Addon>
                    </Space.Compact>
                ) : (
                    <Input
                        value={band.value}
                        onChange={(e) => props.changeValue(false, index, e)}
                        onBlur={(e) => props.changeValue(true, index, e)}
                        prefix={props.valueLeftLabel}
                        status={showValueMessage && band.valueMessage ? 'error' : ''}
                        style={{ width: '100%' }}
                    />
                )}
                    {errorLabel(showValueMessage, band.valueMessage)}
                </div>
            </Col>
            <Col span={3}>
                {props.canDelete && (
                    <div style={{ paddingTop: 30 }}>
                        <Button icon={<DeleteOutlined />} onClick={() => props.deleteTier(index)} danger />
                    </div>
                )}
            </Col>
        </Row>
    );
}

interface CulmulativeBandEditorProps {
    bands: Readonly<EditableDiscountBand>[];
    onUpdateBands: (bands: Readonly<EditableDiscountBand>[]) => void;
    showValidation: boolean;
    discountValueType: DiscountValueType;
    readOnly: boolean;
    currencySymbol?: string;
    currencyCode?: string;
}

interface CulmulativeBandEditorState {}

function calculateFromValue(previousToValue: string) {
    if (!isAllowedBandValue(previousToValue) || previousToValue === '') return '';
    return (parseInt(previousToValue, 10) + 1).toString();
}

export class CulmulativeBandEditor extends React.Component<CulmulativeBandEditorProps, CulmulativeBandEditorState> {
    constructor(props: CulmulativeBandEditorProps) {
        super(props);
        this.state = { bands: [EmptyCulmulativeBand] };
    }

    render() {
        const canDelete = this.props.bands.length > 1;
        const valueLeftLabel =
            this.props.discountValueType === DiscountValueType.AmountPerUnit ||
            this.props.discountValueType === DiscountValueType.OverrideUnitPrice
                ? this.props.currencySymbol || undefined
                : undefined;
        const valueRightLabel =
            this.props.discountValueType === DiscountValueType.AmountPerUnit ||
            this.props.discountValueType === DiscountValueType.OverrideUnitPrice
                ? this.props.currencyCode || '???'
                : '%';

        return (
            <div>
                {this.props.bands.map((band, index) => (
                    <CulmativeBand
                        showValidation={this.props.showValidation}
                        key={index}
                        readOnly={index > 0}
                        canDelete={canDelete}
                        index={index}
                        band={band}
                        changeFrom={this.changeFrom}
                        changeTo={this.changeTo}
                        changeValue={this.changeValue}
                        deleteTier={this.deleteTier}
                        discountValueType={this.props.discountValueType}
                        valueLeftLabel={valueLeftLabel}
                        valueRightLabel={valueRightLabel}
                    />
                ))}
                <Button onClick={this.addTier} disabled={this.props.readOnly} style={{ marginTop: 8 }}>
                    Add tier
                </Button>
            </div>
        );
    }

    private addTier = () => {
        const newBands = this.props.bands.slice();
        const from =
            newBands.length !== 0
                ? (parseInt(newBands[newBands.length - 1].to, 10) + 1).toString()
                : '';
        const newBand = setFrom(EmptyCulmulativeBand, from, true, this.props.discountValueType);
        newBands.push(newBand);

        this.props.onUpdateBands(newBands);
    };

    private updateBand = (index: number, band: Readonly<EditableDiscountBand>) => {
        const newBands = this.props.bands.slice();
        newBands[index] = band;
        this.props.onUpdateBands(newBands);
    };

    private changeFrom = (normalize: boolean, index: number, evt: React.SyntheticEvent<HTMLInputElement>) => {
        const value = evt.currentTarget.value;
        const band = this.props.bands[index];
        const newBand = setFrom(band, value, normalize, this.props.discountValueType);
        if (band === newBand) return;
        this.updateBand(index, newBand);
    };

    private changeTo = (normalize: boolean, index: number, evt: React.SyntheticEvent<HTMLInputElement>) => {
        if (!isAllowedBandValue(evt.currentTarget.value)) return;

        const value = evt.currentTarget.value;
        const oldBand = this.props.bands[index];

        const newBand = setTo(oldBand, value, normalize, this.props.discountValueType);
        if (newBand === oldBand) return;

        const newBands = this.props.bands.slice();
        newBands[index] = newBand;

        const nextIndex = index + 1;
        if (nextIndex < newBands.length) {
            const nextBand = newBands[nextIndex];
            const nextBandFrom = calculateFromValue(evt.currentTarget.value);
            newBands[nextIndex] = setFrom(
                nextBand,
                nextBandFrom,
                normalize,
                this.props.discountValueType
            );
        }

        this.props.onUpdateBands(newBands);
    };

    private changeValue = (normalize: boolean, index: number, evt: React.SyntheticEvent<HTMLInputElement>) => {
        const oldBand = this.props.bands[index];
        const newBand = setValue(oldBand, evt.currentTarget.value, this.props.discountValueType, normalize);
        if (newBand === oldBand) return;
        this.updateBand(index, newBand);
    };

    private deleteTier = (index: number) => {
        const newBands = this.props.bands.slice();
        newBands.splice(index, 1);

        const previousIndex = index - 1;
        const nextIndex = index;
        if (nextIndex < newBands.length && previousIndex >= 0) {
            const newToValue = parseInt(newBands[previousIndex].to, 10) + 1;
            newBands[nextIndex] = setFrom(
                newBands[nextIndex],
                newToValue.toString(),
                true,
                this.props.discountValueType
            );
        }

        this.props.onUpdateBands(newBands);
    };
}
